import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getMissingDocuments, sendDocReminder, linkDocToTxn, getDocReminders } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Receipt, FileWarning, DollarSign, Calendar, Bell, Paperclip, Send, CheckCircle2, AlertTriangle } from 'lucide-react';

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E5E3DB' }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: '#9CA3AF' }}>{label}</p>
          <p className="text-2xl font-heading font-medium" style={{ color: '#1A1A1A' }}>{value}</p>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon size={20} style={{ color }} strokeWidth={1.8} />
        </div>
      </div>
    </div>
  );
}

export default function MissingDocumentsPage() {
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [docRes, remRes] = await Promise.all([
        getMissingDocuments(period || undefined),
        getDocReminders()
      ]);
      setData(docRes.data);
      setReminders(remRes.data.reminders || []);
    } catch { toast.error('Failed to load document data'); }
    setLoading(false);
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReminder = async (txnId) => {
    try {
      await sendDocReminder(txnId, { type: 'email', message: 'Please provide the missing receipt/invoice.' });
      toast.success('Reminder sent');
      fetchData();
    } catch { toast.error('Failed to send reminder'); }
  };

  const handleLink = async (txnId, docType) => {
    try {
      await linkDocToTxn(txnId, { doc_type: docType });
      toast.success('Document linked');
      fetchData();
    } catch { toast.error('Failed to link'); }
  };

  if (loading) return <Layout title="Missing Documents"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D98A4A', borderTopColor: 'transparent' }}></div></div></Layout>;

  const { missing_receipts = [], missing_invoices = [], high_dollar_missing = [], statement_gaps = [], period_blockers = {}, total_missing = 0, missing_receipt_count = 0, missing_invoice_count = 0, high_dollar_count = 0 } = data || {};

  return (
    <Layout title="Missing Documents">
      <div data-testid="missing-documents-page" className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Missing" value={total_missing} icon={FileWarning} color="#C84B31" />
          <StatCard label="Missing Receipts" value={missing_receipt_count} icon={Receipt} color="#D98A4A" />
          <StatCard label="Missing Invoices" value={missing_invoice_count} icon={FileText} color="#D98A4A" />
          <StatCard label="High Dollar" value={high_dollar_count} icon={DollarSign} color="#C84B31" />
          <StatCard label="Statement Gaps" value={statement_gaps.length} icon={Calendar} color="#4B5563" />
        </div>

        {/* Statement Gaps Alert */}
        {statement_gaps.length > 0 && (
          <div className="bg-white rounded-xl p-5 flex items-start gap-3" style={{ border: '1px solid rgba(244,162,97,0.3)', background: 'rgba(244,162,97,0.04)' }} data-testid="statement-gaps">
            <AlertTriangle size={18} style={{ color: '#D98A4A' }} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-body font-medium mb-1" style={{ color: '#1A1A1A' }}>Statement Gaps Detected</p>
              <p className="text-xs font-body" style={{ color: '#4B5563' }}>
                No reconciliation statements uploaded for: {statement_gaps.map((g, i) => (
                  <span key={g} className="inline-block px-2 py-0.5 rounded-full font-mono text-[10px] mx-0.5 mb-0.5" style={{ background: 'rgba(244,162,97,0.15)', color: '#D98A4A' }}>{g}</span>
                ))}
              </p>
            </div>
          </div>
        )}

        {/* Period Blockers */}
        {Object.keys(period_blockers).length > 0 && (
          <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
            <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E3DB' }}>
              <h3 className="font-heading text-lg font-medium">Unresolved by Period</h3>
              <select value={period} onChange={(e) => { setPeriod(e.target.value); setLoading(true); }} className="text-xs font-body rounded-lg px-3 py-1.5" style={{ border: '1px solid #E5E3DB' }} data-testid="period-filter">
                <option value="">All periods</option>
                {Object.keys(period_blockers).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: '#E5E3DB' }}>
              {Object.entries(period_blockers).slice(0, 8).map(([p, info]) => (
                <button key={p} onClick={() => { setPeriod(p); setLoading(true); }} className="bg-white p-4 text-left hover:bg-[rgba(198,156,109,0.02)] transition-colors" data-testid={`period-${p}`}>
                  <p className="font-mono text-xs font-semibold" style={{ color: '#1A1A1A' }}>{p}</p>
                  <p className="text-xs font-body" style={{ color: '#C84B31' }}>{info.count} missing</p>
                  <p className="text-[10px] font-mono" style={{ color: '#9CA3AF' }}>${info.total_amount.toLocaleString()}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Document Lists */}
        <Tabs defaultValue="receipts">
          <TabsList className="p-1 rounded-xl" style={{ background: '#E5E3DB' }}>
            <TabsTrigger value="receipts" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-missing-receipts">
              <Receipt size={14} className="mr-1.5" /> Receipts ({missing_receipt_count})
            </TabsTrigger>
            <TabsTrigger value="invoices" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-missing-invoices">
              <FileText size={14} className="mr-1.5" /> Invoices ({missing_invoice_count})
            </TabsTrigger>
            <TabsTrigger value="high-dollar" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-high-dollar">
              <DollarSign size={14} className="mr-1.5" /> High Dollar ({high_dollar_count})
            </TabsTrigger>
            <TabsTrigger value="reminders" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-reminders">
              <Bell size={14} className="mr-1.5" /> Reminders ({reminders.length})
            </TabsTrigger>
          </TabsList>

          {[
            { key: 'receipts', items: missing_receipts, docType: 'receipt' },
            { key: 'invoices', items: missing_invoices, docType: 'invoice' },
            { key: 'high-dollar', items: high_dollar_missing, docType: 'receipt' },
          ].map(({ key, items, docType }) => (
            <TabsContent key={key} value={key} className="mt-4">
              <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
                {items.length > 0 ? items.map((txn) => (
                  <div key={txn.transaction_id} className="px-5 py-3 flex items-center gap-4 hover:bg-[rgba(198,156,109,0.02)] transition-colors" style={{ borderBottom: '1px solid #E5E3DB' }} data-testid={`doc-${txn.transaction_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-body font-medium truncate" style={{ color: '#1A1A1A' }}>{txn.vendor_name || 'Unknown'}</span>
                        <span className="font-mono text-xs" style={{ color: '#9CA3AF' }}>{txn.date}</span>
                        {txn.reminder_sent && <span className="px-1.5 py-0.5 rounded text-[9px] font-body" style={{ background: 'rgba(198,156,109,0.1)', color: '#D98A4A' }}>Reminded ({txn.reminder_count}x)</span>}
                      </div>
                      <p className="text-xs font-body truncate" style={{ color: '#4B5563' }}>{txn.description}</p>
                    </div>
                    <span className="font-mono text-sm font-medium flex-shrink-0" style={{ color: '#1A1A1A' }}>
                      ${Math.abs(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => handleLink(txn.transaction_id, docType)} className="px-2.5 py-1 rounded-lg text-[10px] font-body font-semibold flex items-center gap-1" style={{ background: 'rgba(45,106,79,0.1)', color: '#2D7A5D' }} data-testid={`link-${txn.transaction_id}`}>
                        <Paperclip size={10} /> Link
                      </button>
                      <button onClick={() => handleReminder(txn.transaction_id)} className="px-2.5 py-1 rounded-lg text-[10px] font-body font-semibold flex items-center gap-1" style={{ background: 'rgba(198,156,109,0.1)', color: '#D98A4A' }} data-testid={`remind-${txn.transaction_id}`}>
                        <Send size={10} /> Remind
                      </button>
                    </div>
                  </div>
                )) : (
                  <div className="p-10 text-center">
                    <CheckCircle2 size={32} className="mx-auto mb-2" style={{ color: '#2D7A5D' }} />
                    <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>All documents linked</p>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}

          {/* Reminders History */}
          <TabsContent value="reminders" className="mt-4">
            <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
              {reminders.length > 0 ? reminders.map((rem) => (
                <div key={rem.reminder_id} className="px-5 py-3 flex items-center gap-4" style={{ borderBottom: '1px solid #E5E3DB' }}>
                  <Bell size={14} style={{ color: '#D98A4A' }} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-body font-medium" style={{ color: '#1A1A1A' }}>{rem.vendor_name}</span>
                    <span className="text-xs font-body ml-2" style={{ color: '#9CA3AF' }}>${Math.abs(rem.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    <p className="text-xs font-body" style={{ color: '#4B5563' }}>{rem.message}</p>
                  </div>
                  <span className="font-mono text-[10px] flex-shrink-0" style={{ color: '#9CA3AF' }}>{new Date(rem.sent_at).toLocaleDateString()}</span>
                </div>
              )) : (
                <div className="p-10 text-center text-sm font-body" style={{ color: '#9CA3AF' }}>No reminders sent yet</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
